import { patientOverviewItemsQueryKey } from '@onestep/camel-api/hooks';
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { logServerError } from '@/utils/logServerError';

import useStores from '../../hooks/useStores';
import { patientEventsBaseQueryKey } from '../usePatientEventsService/consts';
import {
  customEventsBasePath,
  logCustomEventsError,
  patientCustomEventsQueryKey,
  setEventFieldMutationKey,
} from './consts';
import type {
  AmendEntryVariables,
  CreateEntryVariables,
  CustomEventEntry,
  DeleteEntryVariables,
  SetFieldOutcome,
  SetFieldResult,
  SetFieldVariables,
} from './useCustomEventsService';

const invalidateCustomEventFeeds = (
  queryClient: QueryClient,
  workspaceUuid: string,
  patientUuid: string,
  options?: { throwOnError: boolean },
) =>
  Promise.all([
    queryClient.invalidateQueries(
      { queryKey: patientCustomEventsQueryKey(workspaceUuid, patientUuid) },
      options,
    ),
    queryClient.invalidateQueries(
      { queryKey: patientEventsBaseQueryKey(workspaceUuid, patientUuid) },
      options,
    ),
    queryClient.invalidateQueries({ queryKey: ['camel', 'patients', 'list'] }, options),
    queryClient.invalidateQueries({ queryKey: ['camel', 'patients', 'counts'] }, options),
    queryClient.invalidateQueries(
      { queryKey: ['camel', 'patients', patientUuid, 'overview'] },
      options,
    ),
    queryClient.invalidateQueries({ queryKey: patientOverviewItemsQueryKey(patientUuid) }, options),
  ]);

export const useCustomEventMutations = ({ patientUuid }: { patientUuid: string }) => {
  const { appStore } = useStores();
  const workspaceUuid = appStore.clinicUuid;
  const queryClient = useQueryClient();

  const createEntryMutation = useMutation<CustomEventEntry, unknown, CreateEntryVariables>({
    mutationFn: async ({ seriesSlug, payload }) => {
      const { data } = await axios.post<CustomEventEntry>(
        `${customEventsBasePath(workspaceUuid, patientUuid)}/${seriesSlug}`,
        payload,
      );
      return data;
    },
    onError: (error, { seriesSlug }) => {
      logCustomEventsError('custom_events_create_error', { seriesSlug }, error);
    },
    onSettled: () => {
      void invalidateCustomEventFeeds(queryClient, workspaceUuid, patientUuid);
    },
  });

  const amendEntryMutation = useMutation<CustomEventEntry, unknown, AmendEntryVariables>({
    mutationFn: async ({ seriesSlug, entryId, payload }) => {
      const { data } = await axios.put<CustomEventEntry>(
        `${customEventsBasePath(workspaceUuid, patientUuid)}/${seriesSlug}/${entryId}`,
        payload,
      );
      return data;
    },
    onError: (error, { seriesSlug }) => {
      logCustomEventsError('custom_events_amend_error', { seriesSlug }, error);
    },
    onSettled: () => {
      void invalidateCustomEventFeeds(queryClient, workspaceUuid, patientUuid);
    },
  });

  const deleteEntryMutation = useMutation<void, unknown, DeleteEntryVariables>({
    mutationFn: async ({ seriesSlug, entryId }) => {
      await axios.delete(
        `${customEventsBasePath(workspaceUuid, patientUuid)}/${seriesSlug}/${entryId}`,
      );
    },
    onError: (error, { seriesSlug }) => {
      logServerError('custom_events_delete_error', 'Failed to delete event', { seriesSlug }, error);
    },
    onSettled: () => {
      void invalidateCustomEventFeeds(queryClient, workspaceUuid, patientUuid);
    },
  });

  return {
    createEntry: createEntryMutation.mutateAsync,
    amendEntry: amendEntryMutation.mutateAsync,
    deleteEntry: deleteEntryMutation.mutate,
  };
};

export const useSetEventFieldMutation = ({ patientUuid }: { patientUuid: string }) => {
  const { appStore } = useStores();
  const workspaceUuid = appStore.clinicUuid;
  const queryClient = useQueryClient();

  const setFieldMutation = useMutation<SetFieldOutcome, unknown, SetFieldVariables>({
    mutationKey: setEventFieldMutationKey(workspaceUuid, patientUuid),
    mutationFn: async ({ seriesSlug, field, value, rtm, linkedAttributeId }) => {
      const { data } = await axios.post<SetFieldResult>(
        `${customEventsBasePath(workspaceUuid, patientUuid)}/${seriesSlug}/set-field`,
        {
          field,
          value,
          ...(rtm === undefined ? {} : { rtm }),
          ...(linkedAttributeId === undefined ? {} : { attributeId: linkedAttributeId }),
        },
      );
      if (linkedAttributeId === undefined) return data;
      const isRefreshed = await invalidateCustomEventFeeds(
        queryClient,
        workspaceUuid,
        patientUuid,
        { throwOnError: true },
      ).then(
        () => true,
        () => false,
      );
      return { ...data, isRefreshFailed: !isRefreshed };
    },
    onError: (error, { seriesSlug, field }) => {
      logCustomEventsError('custom_events_set_field_error', { seriesSlug, field }, error);
    },
    onSettled: (_result, error, { linkedAttributeId }) => {
      if (linkedAttributeId === undefined || error) {
        void invalidateCustomEventFeeds(queryClient, workspaceUuid, patientUuid);
      }
    },
  });

  return {
    setField: setFieldMutation.mutateAsync,
    isSettingField: setFieldMutation.isLoading,
  };
};
